import { type CSSProperties, type FC } from "react"
import { contentfulImageUrl } from "~/helpers/contentful"
import {
  MOBILE_SRCSET_WIDTHS,
  DESKTOP_SRCSET_WIDTHS,
  MOBILE_MAX_WIDTH_PX,
  calculateAspectRatios,
  parseSizesAttribute
} from "~/helpers/responsiveImage"

declare module "react" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ImgHTMLAttributes<T> {
    fetchpriority?: "high" | "low" | "auto"
  }
}

// See https://www.contentful.com/developers/docs/references/images-api

interface Props {
  src: string
  srcMobile?: string
  alt: string
  placeholder?: string
  // Intrinsic image dimensions to prevent layout shifts.
  width?: number
  height?: number
  // Mobile-specific dimensions (used when srcMobile is provided)
  widthMobile?: number
  heightMobile?: number

  // Hint to the browser how large the image will be (e.g. 20vw or 100vw).
  sizeMobile?: string
  sizeDesktop?: string

  // Override sizes attribute for more precise control
  sizes?: string

  className?: string
  style?: CSSProperties
  fit?: "pad" | "fill" | "scale" | "crop" | "thumb"
  focus?:
    | "center"
    | "top"
    | "right"
    | "left"
    | "bottom"
    | "top_right"
    | "top_left"
    | "bottom_right"
    | "bottom_left"
    | "face"
    | "faces"
  lazyLoad?: boolean
}

const ResponsiveContentfulImage: FC<Props> = ({
  src,
  srcMobile,
  alt,
  sizeMobile = "100vw",
  sizeDesktop = "100vw",
  sizes,
  width,
  height,
  widthMobile,
  heightMobile,
  fit = "fill",
  focus = "center",
  lazyLoad = true,
  style,
  ...props
}) => {
  const mobileImageUrl = srcMobile || src
  const desktopImageUrl = src
  const hasSeparateMobileImage = !!srcMobile && srcMobile !== src

  // Use mobile dimensions if provided and we have a separate mobile image
  const imgWidth = hasSeparateMobileImage && widthMobile ? widthMobile : width
  const imgHeight =
    hasSeparateMobileImage && heightMobile ? heightMobile : height

  // Calculate aspect ratios and parse sizes attribute
  const aspectRatios = calculateAspectRatios(
    width,
    height,
    widthMobile,
    heightMobile,
    hasSeparateMobileImage
  )
  const parsedSizes = parseSizesAttribute(sizes, sizeMobile, sizeDesktop)

  // Build responsive aspect-ratio style
  // Prefer mobile aspect ratio when available, fall back to desktop
  const pictureStyle: CSSProperties = {}
  if (aspectRatios.mobile || aspectRatios.desktop) {
    pictureStyle.aspectRatio = aspectRatios.mobile || aspectRatios.desktop
  }

  return (
    <picture className="relative w-full" style={pictureStyle}>
      {/* Mobile sources - always include for mobile viewports to use mobile-optimized widths */}
      <source
        media={`(max-width: ${MOBILE_MAX_WIDTH_PX}px)`}
        srcSet={MOBILE_SRCSET_WIDTHS.map(
          width =>
            `${contentfulImageUrl(mobileImageUrl, { width, format: "avif", quality: 75, fit, focus })} ${width}w`
        ).join(", ")}
        sizes={parsedSizes.mobile}
        type="image/avif"
      />
      <source
        media={`(max-width: ${MOBILE_MAX_WIDTH_PX}px)`}
        srcSet={MOBILE_SRCSET_WIDTHS.map(
          width =>
            `${contentfulImageUrl(mobileImageUrl, { width, format: "webp", quality: 75, fit, focus })} ${width}w`
        ).join(", ")}
        sizes={parsedSizes.mobile}
        type="image/webp"
      />

      {/* Desktop sources - for desktop viewports only */}
      <source
        media={`(min-width: ${MOBILE_MAX_WIDTH_PX + 1}px)`}
        srcSet={DESKTOP_SRCSET_WIDTHS.map(
          width =>
            `${contentfulImageUrl(desktopImageUrl, { width, format: "avif", quality: 75, fit, focus })} ${width}w`
        ).join(", ")}
        sizes={parsedSizes.desktop}
        type="image/avif"
      />
      <source
        media={`(min-width: ${MOBILE_MAX_WIDTH_PX + 1}px)`}
        srcSet={DESKTOP_SRCSET_WIDTHS.map(
          width =>
            `${contentfulImageUrl(desktopImageUrl, { width, format: "webp", quality: 75, fit, focus })} ${width}w`
        ).join(", ")}
        sizes={parsedSizes.desktop}
        type="image/webp"
      />
      <img
        src={contentfulImageUrl(mobileImageUrl, {
          format: "webp",
          width: MOBILE_MAX_WIDTH_PX,
          quality: 75,
          fit,
          focus
        })}
        sizes={parsedSizes.fallback}
        loading={lazyLoad ? "lazy" : "eager"}
        fetchPriority={lazyLoad ? undefined : "high"}
        decoding="async"
        alt={alt}
        width={imgWidth}
        height={imgHeight}
        style={{ height: "auto", ...style }}
        {...props}
      />
    </picture>
  )
}

export default ResponsiveContentfulImage
